import { createContext, useContext, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useMastersQuery, type MastersData } from '../hooks/useMastersQuery';
import type { CodeMaster, SelectOption } from '../types'
import React from 'react';

// Contextが提供する値の型を拡張
interface MastersContextType extends MastersData {
  isMastersLoading: boolean;
  teaTypes: SelectOption[];
  teaSeasons: SelectOption[];
  varieties: SelectOption[];
}

const MastersContext = createContext<MastersContextType | null>(null);

const getOptionsByCategory = (
  masters: CodeMaster[], 
  category: string
  ) => {
    return masters
      .filter(m => m.category === category) // カテゴリで絞り込み、削除済みを除外
      .map(m => ({
        id: m.code.toString(), // selectのvalueは通常stringなので変換
        name: m.name,
      }));
};
// type SelectOption = ReturnType<typeof getOptionsByCategory>[number];

/**
 * マスターデータをReact Queryで取得し、アプリケーション全体に提供するProvider
 */
export const MastersProvider = ({ children }: { children: ReactNode }) => {
  // ログイン状態を取得して、データ取得を有効化/無効化する
  const { currentUser } = useAuth();
  
  // useMastersQueryフックを呼び出し、データとローディング状態を取得
  // currentUserが存在する場合(isLoggedIn = true)にのみ、データ取得が実行される
  const { data, isLoading, isError } = useMastersQuery(!!currentUser);

  const teaTypes: SelectOption[] = React.useMemo(() => {
    if (data?.masters){
      return getOptionsByCategory(data?.masters, 'TEA_TYPE');
    }
    return [];
  }, [data?.masters]); // mastersが更新された時だけ実行

  const teaSeasons: SelectOption[] = React.useMemo(() => {
    if (data?.masters){
      return getOptionsByCategory(data?.masters, 'TEA_SEASON');
    }
    return [];
  }, [data?.masters]); // mastersが更新された時だけ実行

  const varieties: SelectOption[] = React.useMemo(() => {
    if (data?.masters){
      return getOptionsByCategory(data?.masters, 'VARIETY');
    }
    return [];
  }, [data?.masters]); // mastersが更新された時だけ実行

  // Contextで提供する値を作成
  // データがまだない場合は、空の配列をデフォルト値として提供
  const value: MastersContextType = {
    teaTypes: teaTypes,
    teaSeasons: teaSeasons,
    varieties: varieties,
    masters: data?.masters || [],
    // teaTypes3: data?.teaTypes3 || [],
    // teaSeasons3: data?.teaSeasons3 || [],
    // varieties3: data?.varieties3 || [],
    sellers: data?.sellers || [],
    farmers: data?.farmers || [],
    // brands: data?.brands || [],
    gaps: data?.gaps || [],
    isMastersLoading: isLoading,
  };
  
  // エラーが発生した場合、子コンポーネントをレンダリングしない、などのハンドリングも可能
  if (isError && !!currentUser) {
      return <div>マスターデータの読み込みに失敗しました。</div>
  }

  return <MastersContext.Provider value={value}>{children}</MastersContext.Provider>;
};

/**
 * MastersContextから値を取り出すためのカスタムフック
 */
export const useMasters = () => {
  const context = useContext(MastersContext);
  if (!context) throw new Error('useMasters must be used within a MastersProvider');
  return context;
};
